import React, { useEffect, useState } from 'react';
import { httpsCallable } from 'firebase/functions';
import Brand from '../Layout/Brand';
import SeoHead from './SeoHead';
import { functions } from '../../services/firebase';
import { trackWebsiteEvent } from '../../services/siteAnalytics';
import '../../styles/marketing.css';

const appStoreURL = process.env.REACT_APP_APP_STORE_URL || '';
const playStoreURL = process.env.REACT_APP_PLAY_STORE_URL || '';
const createCheckout = httpsCallable(functions, 'createStripeCheckoutSession');
const createPortal = httpsCallable(functions, 'createStripePortalSession');

function StoreLinks() {
  return (
    <div className="store-links">
      {appStoreURL ? <a href={appStoreURL} onClick={() => trackWebsiteEvent('app_store_clicked', { location: 'plans' })}>Download on the App Store</a> : <span className="store-coming-soon"><b>Pending Approval</b> App Store</span>}
      {playStoreURL ? <a href={playStoreURL}>Get it on Google Play</a> : <span className="store-coming-soon"><b>Coming Soon</b> Google Play</span>}
    </div>
  );
}

export default function PlansPage({ user, onSignIn, onSignOut, authError }) {
  const selectedPlan = new URLSearchParams(window.location.search).get('plan');
  const checkoutResult = new URLSearchParams(window.location.search).get('checkout');
  const [busy, setBusy] = useState('');
  const [message, setMessage] = useState('');
  useEffect(() => {
    if (checkoutResult === 'success') trackWebsiteEvent('checkout_succeeded', { plan: 'live' });
    if (checkoutResult === 'cancelled') trackWebsiteEvent('checkout_cancelled', { plan: 'live' });
  }, [checkoutResult]);
  const beginCheckout = async (interval) => {
    trackWebsiteEvent('checkout_started', { interval, plan: 'live' });
    if (!user) { trackWebsiteEvent('sign_in_started', { location: 'checkout' }); await onSignIn(); return; }
    setBusy(interval); setMessage('Opening secure checkout…');
    try {
      const result = await createCheckout({ interval });
      window.location.assign(result.data.url);
    } catch (error) {
      trackWebsiteEvent('checkout_failed', { interval, result: 'error' });
      setMessage(error?.code === 'functions/already-exists'
        ? 'LeaveNOW Live is already active for this account.'
        : 'Checkout could not start. Please try again or contact billing@getleavenow.com.');
      setBusy('');
    }
  };
  const manageBilling = async () => {
    if (!user) { trackWebsiteEvent('sign_in_started', { location: 'billing_portal' }); await onSignIn(); return; }
    setBusy('portal'); setMessage('Opening billing…');
    try {
      trackWebsiteEvent('billing_portal_opened');
      const result = await createPortal({});
      window.location.assign(result.data.url);
    } catch {
      setMessage('No web subscription is connected to this account yet.');
      setBusy('');
    }
  };
  const planSchema = {
    '@context': 'https://schema.org',
    '@type': 'MobileApplication',
    name: 'LeaveNOW',
    applicationCategory: 'TravelApplication',
    operatingSystem: 'iOS',
    url: 'https://getleavenow.com/plans',
    offers: [
      { '@type': 'Offer', name: 'LeaveNOW Free', price: '0', priceCurrency: 'USD' },
      { '@type': 'Offer', name: 'LeaveNOW Live Monthly', price: '4.99', priceCurrency: 'USD' },
      { '@type': 'Offer', name: 'LeaveNOW Live Annual', price: '39.99', priceCurrency: 'USD' },
    ],
  };
  return (
    <div className="checkout-site">
      <SeoHead
        title="LeaveNOW Pricing | Free and Live Departure Alerts"
        description="Compare LeaveNOW Free with LeaveNOW Live at $4.99 monthly or $39.99 annually for continuous traffic monitoring and updated departure alerts."
        path="/plans"
        schema={planSchema}
      />
      <header className="checkout-header"><a href="/" aria-label="LeaveNOW home"><Brand /></a><a href="/">Back to LeaveNOW</a></header>
      <main className="checkout-main">
        <div className="checkout-intro">
          <p className="marketing-eyebrow">Confidence Delivered</p>
          <h1>Choose how LeaveNOW protects your time.</h1>
          <p>Start free or turn on continuous traffic monitoring with LeaveNOW Live. Your account follows you into the app.</p>
          {checkoutResult === 'success' && <p className="checkout-status success">Payment confirmed. LeaveNOW Live will appear in the app after entitlement verification.</p>}
          {checkoutResult === 'cancelled' && <p className="checkout-status">Checkout cancelled. Nothing was charged.</p>}
          {(message || authError) && <p className="checkout-status">{message || authError}</p>}
          <p className="checkout-account">{user
            ? <>Signed in as <strong>{user.email}</strong> · <button type="button" onClick={onSignOut}>Sign out</button></>
            : <>Sign in once, then choose monthly or annual billing.</>}</p>
        </div>
        <div className="checkout-plans">
          <article className={selectedPlan === 'free' ? 'selected' : ''}>
            <p>LeaveNOW</p><h2>Free</h2><span>Plan trips and receive the original departure recommendation.</span>
            <ul><li>Trip planning</li><li>Core departure recommendations</li><li>Saved places</li><li>Scheduled departure reminders</li><li>Dynamic Island and Lock Screen card</li><li>Navigation handoff</li></ul>
            <a className="checkout-button secondary" href="#download">Continue free</a>
          </article>
          <article className={`live ${selectedPlan === 'live' ? 'selected' : ''}`}>
            <div className="popular-label">MOST CONFIDENCE</div>
            <p>LeaveNOW Live</p><h2>$4.99 <small>/ month</small></h2><span>Continuous monitoring for commitments that cannot be left to guesswork.</span>
            <ul><li>Continuous trip monitoring</li><li>Automatic traffic recalculation</li><li>Updated leave recommendations</li><li>Updated Lock Screen guidance</li></ul>
            {appStoreURL ? <>
              <a className="checkout-button" href={appStoreURL} onClick={() => trackWebsiteEvent('app_store_clicked', { location: 'plans_monthly' })}>Subscribe in the app</a>
              <a className="annual-option annual-button" href={appStoreURL} onClick={() => trackWebsiteEvent('app_store_clicked', { location: 'plans_annual' })}>Or $39.99 annually — save 33%</a>
            </> : <>
              <button className="checkout-button" disabled={Boolean(busy)} onClick={() => beginCheckout('monthly')}>
                {busy === 'monthly' ? 'Opening checkout…' : user ? 'Subscribe monthly' : 'Sign in to subscribe'}
              </button>
              <button className="annual-option annual-button" disabled={Boolean(busy)} onClick={() => beginCheckout('annual')}>Or $39.99 annually — save 33%</button>
            </>}
            <button className="billing-link" disabled={Boolean(busy)} onClick={manageBilling}>Manage or cancel web subscription</button>
          </article>
        </div>
        <section className="download-step" id="download">
          <p className="marketing-eyebrow">Next step</p>
          <h2>Install LeaveNOW and create your account.</h2>
          <p>Free customers register in the app. Paid customers use the same email used at checkout so their Live entitlement can be activated automatically.</p>
          <StoreLinks />
        </section>
        <p className="checkout-trust">Easy cancellation. No advertising. We do not sell identifiable location history. <a href="/privacy">Privacy</a> · <a href="https://www.apple.com/legal/internet-services/itunes/dev/stdeula/">Terms</a></p>
      </main>
    </div>
  );
}
